import React, { useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import styled from "styled-components";

import { ExclamationCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { displayedMRN } from "@tailormed/common-client/util/mrn";
import { Button, Popover, Space, Spin, Tooltip, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { starFilterList, starPatient } from "../../api/api";
import ActiveStarIcon from "../../assets/svg/ActiveStarIcon";
import InActiveStarIcon from "../../assets/svg/InActiveStarIcon";
import { ReactComponent as AlertTrimmed } from "../../assets/svg/alert-circle-trimmed.svg";
import editPencilIcon from "../../assets/svg/edit-pencil-icon.svg";
import {
  BLACK,
  ERROR_50,
  ERROR_600,
  GRAY_100,
  GRAY_400,
  GRAY_500,
  GRAY_600,
  GRAY_700,
  GRAY_900,
  PRIMARY_50,
  PRIMARY_600,
  SUCCESS_50,
  SUCCESS_600,
  WARNING_50,
  WARNING_600,
  WHITE,
  GRAY_800
} from "../../constant/colors";
import { Routes } from "../../constant/routes";
import { fontWeights, sizes } from "../../constant/styles";
import { useGetSetting } from "../../hooks/getSetting";
import ErrorMessage from "../customComponent/customMessages/ErrorMessage";
import "./TooltipArrow.css";

import DrugsIcon from "../../assets/svg/DrugsIcon";
import { ReactComponent as HasConflictIcon } from "../../assets/svg/has-conflicts-icon.svg";
import { ReactComponent as UserIcon } from "../../assets/svg/user-icon.svg";
import { maxNumberOfRecords, selectedViewTypes } from "../../constant/table";
import {
  calculateTotalViewersCount,
  getViewersTooltipContent,
  parseViewers
} from "@tailormed/common-client/util/savedList";
import { SHARED, getTypeIcon } from "../../constant/savedList";
import ACTIONS from "../../store/action";
import {
  selectedViewTableData,
  selectPatientDetails,
  selectUser,
  selectUserOrGroupById,
  selectAllUsersAndUserGroups,
  selectUserGroupsWithUsers
} from "../../store/selector";
import { capitalizeEachFirstLetter } from "../../utils/string";

import moment from "moment-timezone";
import { Link } from "react-router-dom";
import { DENIED, NEW_PENDING_PAYMENT, OOP, PAID, PENDING, statuses, SUBMITTED } from "../../constant/claim";
import { customerTypes } from "../../constant/customerType";
import { ASCEND, DECEND, SORT_DIRECTIONS_DEFAULT } from "../../constant/sort";
import { DATE_FORMAT, dateFormatter } from "../../utils/date";
import { formatAssignee, formatPhoneNumber, formatSSN } from "../../utils/formaters";
import { TitledTooltip } from "../TitledTooltip";
const { Text } = Typography;

const StyledStarButton = styled(Button)`
  padding: 0;
  margin-top: 5px;
  background-color: transparent !important;

  &:hover,
  &:focus,
  &:active {
    background-color: transparent !important;
  }
`;

export const HasConflictsColumn = ({ record }) => {
  const { t } = useTranslation();

  const hasConflicts = false;
  //record.hasDemographicsConflicts || record.hasDiagnosesConflicts || record.hasInsuranceConflicts;
  const history = useHistory();
  const { patientId, journeyId } = record;

  return (
    <div style={{ paddingTop: "8px", display: "flex", flexDirection: "column", gap: "5px" }}>
      {hasConflicts && (
        <Tooltip title={<Text>{t("view_conflicts")}</Text>} color={WHITE}>
          <div onClick={() => history.push(`/${Routes.PATIENT}${patientId}/journey/${journeyId}/edit`)}>
            <HasConflictIcon id={`patient-has-conflicts-${patientId}`} style={{ height: "17", width: "17" }} />
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export const EditColumn = ({ record, isHovering }) => {
  const { t } = useTranslation();
  const { patientId, journeyId } = record;

  return (
    <div style={{ minWidth: "60px", display: "flex", justifyContent: "center" }}>
      {isHovering && (
        <Link
          id="patient_list_edit_patient"
          type="text"
          style={{ padding: 0 }}
          to={`/${Routes.PATIENT}${patientId}/journey/${journeyId}/edit`}
        >
          <Tooltip title={<Text>{t("edit_patient_details")}</Text>} color={WHITE}>
            <img src={editPencilIcon} alt="edit pencil icon" />
          </Tooltip>
        </Link>
      )}
    </div>
  );
};

export const PatientNameColumn = ({ oneLine, record }) => {
  const { t } = useTranslation();
  const [prefix, customerType] = useGetSetting(["id_prefix", "fe-customer-type"]);

  const patientColumnParentStyle = oneLine ? {} : { display: "flex", flexDirection: "column" };
  return (
    <div style={patientColumnParentStyle}>
      <Text
        style={{
          fontWeight: fontWeights.semibold,
          fontSize: sizes.medium,
          color: GRAY_900
        }}
        id="patient_name"
      >
        {record.name ? record.name : "Patient"}
      </Text>
      <span
        style={{
          display: oneLine ? "inline" : "flex",
          paddingLeft: oneLine ? "5px" : "",
          flexDirection: oneLine ? "" : "row",
          gap: oneLine ? "" : "5px",
          alignItems: oneLine ? "" : "center",
          lineHeight: oneLine ? "35px" : "",
          fontSize: oneLine ? sizes.medium : sizes.small,
          fontWeight: fontWeights.regular
        }}
      >
        {displayedMRN(record.mrn) && (
          <Text style={{ color: GRAY_500 }} id="patient_mrn">
            {customerType.toLowerCase() === customerTypes.RETAIL
              ? t("columns.record_phone", { mrnRecord: formatPhoneNumber(record.mrn) })
              : t("columns.record_mrn", { mrnRecord: displayedMRN(record.mrn) })}
          </Text>
        )}
        <Text
          style={{ color: GRAY_500, paddingLeft: oneLine && displayedMRN(record.mrn) ? "5px" : "" }}
          id="patient_id"
        >{`(${prefix ? prefix.toUpperCase() : "TM"}-${record.patientId})`}</Text>
      </span>
    </div>
  );
};

export const ClaimStatusColumn = ({ status, isActive }) => {
  const statusLabel = statuses.find((item) => item.value === status)?.label;
  return (
    <Text
      style={{
        background:
          status === PAID
            ? SUCCESS_50
            : status === DENIED
            ? ERROR_50
            : status === SUBMITTED
            ? PRIMARY_50
            : status === PENDING
            ? GRAY_100
            : status === NEW_PENDING_PAYMENT
            ? WARNING_50
            : status === OOP
            ? GRAY_100
            : "",
        borderRadius: "16px",
        color:
          status === PAID
            ? SUCCESS_600
            : status === DENIED
            ? ERROR_600
            : status === SUBMITTED
            ? PRIMARY_600
            : status === PENDING
            ? GRAY_600
            : status === NEW_PENDING_PAYMENT
            ? WARNING_600
            : status === OOP
            ? GRAY_600
            : "",
        fontWeight: fontWeights.bold,
        fontSize: sizes.medium,
        display: "flex",
        justifyContent: "center",
        width: status === NEW_PENDING_PAYMENT ? "140px" : status === OOP ? "120px" : "91px",
        opacity: isActive === false && "0.3",
        cursor: "pointer"
      }}
    >
      {status === OOP ? statusLabel : capitalizeEachFirstLetter(statusLabel)}
    </Text>
  );
};

export const AssigneeColumn = ({ record }) => {
  const assigneesTextToDisplay = record?.assignees
    ?.map((assignee) => assignee.userName || assignee.assigneeName)
    .sort()
    .join(" | ");
  return (
    <Tooltip title={<Text>{assigneesTextToDisplay}</Text>} color={WHITE} style={{ cursor: "pointer" }}>
      <Text ellipsis={true} style={{ maxWidth: "200px" }}>
        {assigneesTextToDisplay}
      </Text>
    </Tooltip>
  );
};

export const DateOfServiceColumn = ({ record }) => {
  return (
    <div style={{ whiteSpace: "nowrap" }}>
      {record.minServiceDate === record.maxServiceDate
        ? dateFormatter(record.minServiceDate)
        : `${dateFormatter(record.minServiceDate)} - ${dateFormatter(record.maxServiceDate)}`}
    </div>
  );
};

export const ListNameColumn = ({ record }) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);

  const getInvalidMessage = () => {
    const isOwner = user?.id === record?.user;
    if (isOwner) {
      return t("savedList.owner_in_valid_list");
    } else {
      return t("savedList.non_owner_in_valid_list");
    }
  };

  const typeIconData = getTypeIcon(record.type);
  const viewers = parseViewers(record.viewers);

  return (
    <div style={{ display: "flex", flexDirection: "row", gap: "12px", alignItems: "center" }}>
      {typeIconData && (
        <Tooltip
          overlayClassName="custom-white-tooltip"
          arrow={{
            pointAtCenter: true
          }}
          overlayInnerStyle={{
            backgroundColor: WHITE,
            color: BLACK,
            borderRadius: 8,
            padding: "10px 15px 0 15px"
          }}
          title={
            <Space direction="vertical">
              <Text style={{ color: BLACK, fontWeight: fontWeights.semibold }}>{typeIconData.subtitle}</Text>
            </Space>
          }
        >
          <div style={{ height: "25px" }} id={typeIconData.id}>
            {typeIconData.icon}
          </div>
        </Tooltip>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "0px" }}>
        <Space>
          <Text
            style={{
              fontWeight: fontWeights.semibold,
              fontSize: sizes.medium,
              color: GRAY_900
            }}
          >
            {record.name ? record.name : t("savedList.no_list_name")}
          </Text>
          {!record.isValid && (
            <Tooltip
              overlayInnerStyle={{
                backgroundColor: BLACK,
                borderRadius: 5,
                padding: "15px"
              }}
              overlayStyle={{
                maxWidth: "450px"
              }}
              autoAdjustOverflow={false}
              title={<Text style={{ color: WHITE, fontWeight: fontWeights.medium }}>{getInvalidMessage()}</Text>}
            >
              <div style={{ paddingTop: "1.5px", position: "relative", display: "flex", alignItems: "center" }}>
                <AlertTrimmed />
              </div>
            </Tooltip>
          )}
        </Space>

        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "25px" }}>
          <Text style={{ fontSize: sizes.medium, color: GRAY_500, fontWeight: fontWeights.regular }}>
            {t("savedList.description", {
              view: selectedViewTypes[record.selectedView].description
            })}
          </Text>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <UserIcon />
            <Text style={{ fontSize: sizes.medium, color: GRAY_500, fontWeight: fontWeights.regular }}>
              {t("savedList.created_by")} {record.username}
            </Text>
          </div>

          {record.type === SHARED && viewers?.length > 0 && (
            <div id="shared_lists_viewers_tag">
              <ViewersTooltip viewers={viewers} owner={record.user} t={t} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const NumberOfRecordsColumn = ({ record, savedListsRecordCounts }) => {
  const { t } = useTranslation();
  const currentRecord = savedListsRecordCounts[record?.id];

  if (!currentRecord) return <Spin />;

  if (currentRecord.success === false) {
    return <Text> {t("savedList.no_records")} </Text>;
  } else if (currentRecord.success) {
    return (
      <Text>{currentRecord.total > maxNumberOfRecords ? t("savedList.more_than_10000") : currentRecord.total}</Text>
    );
  }

  return null;
};

const StarColumnComponent = ({
  record,
  isRecentPatient,
  patientDetails,
  updatePatientDetailsAct,
  setPatientTableRecordDataAct,
  updateSearchedPatientAct,
  updateRecentPatientAct
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const { t } = useTranslation();
  const title = t(record?.starred ? "favorite" : "mark_as_favorite");

  const handleStarPatient = async (patientId, newValue) => {
    try {
      setIsFetching(true);
      await starPatient(patientId, newValue);
      setPatientTableRecordDataAct(patientId, { starred: newValue });
      if (isRecentPatient) {
        updateRecentPatientAct(patientId, { starred: newValue });
      } else {
        updateSearchedPatientAct(patientId, { starred: newValue });
      }
      if (patientDetails && patientDetails.id === patientId) {
        updatePatientDetailsAct({ ...patientDetails, starred: newValue });
      }
    } catch (error) {
      ErrorMessage(`${t("errorMessages.patient_starred")} ${error}`);
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <div onClick={(event) => event.stopPropagation()}>
      {isFetching && <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />}
      {!isFetching && (
        <Button type="text" onClick={() => handleStarPatient(record.patientId, !record.starred)} style={{ padding: 0 }}>
          <Tooltip
            title={
              <Text style={{ color: BLACK, fontSize: sizes.small, fontWeight: fontWeights.semibold }}>{t(title)}</Text>
            }
            color={WHITE}
          >
            <div
              style={{
                minWidth: 18,
                minHeight: 18
              }}
              id={record.starred ? "remove_from_favorite" : "mark_list_as_favorite"}
            >
              {record.starred ? <ActiveStarIcon /> : <InActiveStarIcon />}
            </div>
          </Tooltip>
        </Button>
      )}
    </div>
  );
};

const starColumnMapDispatchToProps = (dispatch) => ({
  updatePatientDetailsAct: (patientDetails) => dispatch(ACTIONS.updatePatientDetailsAction(patientDetails)),
  updateSearchedPatientAct: (patientId, sharedFields) =>
    dispatch(ACTIONS.updateSearchedPatient(patientId, sharedFields)),
  updateRecentPatientAct: (patientId, sharedFields) => dispatch(ACTIONS.updateRecentPatient(patientId, sharedFields)),
  setPatientTableRecordDataAct: (patientId, sharedFields) =>
    dispatch(ACTIONS.setPatientTableRecordData(patientId, sharedFields))
});
const starColumnMapStateToProps = createStructuredSelector({
  patientDetails: selectPatientDetails
});
const StarColumn = compose(connect(starColumnMapStateToProps, starColumnMapDispatchToProps))(StarColumnComponent);

export { StarColumn };

const StarFilterListColumn = ({ record, page }) => {
  const dispatch = useDispatch();
  const [isFetching, setIsFetching] = useState(false);
  const [localStarred, setLocalStarred] = useState(record.starred);
  const { t } = useTranslation();

  React.useEffect(() => {
    setLocalStarred(record.starred);
  }, [record.starred]);

  const title = t(localStarred ? "favorite" : "mark_as_favorite");

  const handleStarFilterList = async (filterListId, newValue) => {
    setLocalStarred(newValue);
    setIsFetching(true);

    try {
      const response = await starFilterList(filterListId, newValue);
      if (response && response.data && response.data.success) {
        dispatch(ACTIONS.searchSavedListAction(page));
      } else {
        setLocalStarred(!newValue);
        ErrorMessage(`${t("errorMessages.filterlist_starred")}`);
      }
    } catch (error) {
      setLocalStarred(!newValue);
      ErrorMessage(`${t("errorMessages.filterlist_starred")}`);
    } finally {
      setTimeout(() => {
        setIsFetching(false);
      }, 500);
    }
  };

  return (
    <>
      {isFetching && <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />}
      {!isFetching && (
        <StyledStarButton
          type="text"
          id={record.starred ? "remove_from_favorite" : "mark_list_as_favorite"}
          onClick={() => handleStarFilterList(record.id, !localStarred)}
        >
          <Tooltip
            title={
              <Text style={{ color: BLACK, fontSize: sizes.small, fontWeight: fontWeights.semibold }}>{t(title)}</Text>
            }
            color={WHITE}
          >
            <div style={{ minWidth: 18, minHeight: 18 }}>
              {localStarred ? <ActiveStarIcon /> : <InActiveStarIcon />}
            </div>
          </Tooltip>
        </StyledStarButton>
      )}
    </>
  );
};

export { StarFilterListColumn };

export const ViewersTooltip = ({ viewers, owner, t }) => {
  const assigneesAndUserGroups = useSelector(selectAllUsersAndUserGroups);
  const userGroupsWithUsers = useSelector(selectUserGroupsWithUsers);

  const viewersArray = parseViewers(viewers);

  if (!viewersArray || viewersArray.length === 0) return null;

  const totalViewersCount = calculateTotalViewersCount(
    viewersArray,
    assigneesAndUserGroups,
    userGroupsWithUsers,
    owner
  );

  return (
    <Tooltip
      placement="top"
      overlayClassName="custom-white-tooltip"
      arrow={{
        pointAtCenter: true
      }}
      autoAdjustOverflow={true}
      overlayInnerStyle={{
        backgroundColor: WHITE,
        color: BLACK,
        borderRadius: 8,
        padding: "10px 15px 0 15px"
      }}
      title={
        <div style={{ color: BLACK }}>
          {getViewersTooltipContent(viewersArray, assigneesAndUserGroups, userGroupsWithUsers, owner)}
        </div>
      }
    >
      <div
        style={{
          borderRadius: 100,
          fontSize: sizes.small,
          fontWeight: fontWeights.bold,
          border: "none",
          backgroundColor: PRIMARY_50,
          color: GRAY_800,
          height: "28px",
          margin: "2px",
          padding: "5px 10px",
          alignItems: "center",
          display: "flex"
        }}
      >
        {totalViewersCount} {totalViewersCount === 1 ? t("savedList.viewer") : t("savedList.viewers")}
      </div>
    </Tooltip>
  );
};

export const DrugColumnComponent = ({ drugs }) => {
  const { t } = useTranslation();

  const associatedDrugs = drugs || "[]";
  if (drugs?.length === 1) {
    return <Text>{drugs[0]}</Text>;
  } else if (drugs?.length > 1) {
    let parsedDrugs = [];
    try {
      parsedDrugs = Array.isArray(associatedDrugs) ? associatedDrugs : JSON.parse(associatedDrugs);
    } catch (error) {
      // If JSON parsing fails, try splitting by comma
      parsedDrugs = typeof associatedDrugs === "string" ? associatedDrugs.split(",").map((drug) => drug.trim()) : [];
    }

    // Ensure parsedDrugs is always an array
    if (!Array.isArray(parsedDrugs)) {
      parsedDrugs = [parsedDrugs];
    }

    const tooltipContent = parsedDrugs
      .slice(0, 50)
      .sort((a, b) => a.localeCompare(b))
      .map((drug) => (
        <p
          key={drug}
          style={{
            fontFamily: "Open Sans",
            fontWeight: fontWeights.regular,
            fontSize: sizes.small,
            lineHeight: "150%",
            margin: 0,
            color: "black",
            display: "flex",
            alignItems: "flex-start",
            gap: "4px"
          }}
        >
          <span
            style={{
              fontSize: "18px",
              lineHeight: "1"
            }}
          >
            •
          </span>
          <Text>{drug}</Text>
        </p>
      ));
    return (
      <>
        {parsedDrugs.length === 0 ? (
          ""
        ) : parsedDrugs.length === 1 ? (
          parsedDrugs[0]
        ) : (
          <Tooltip
            title={parsedDrugs?.length && <div style={{ color: "black", padding: "0 10px" }}>{tooltipContent}</div>}
            color={WHITE}
            overlayStyle={{ borderRadius: "8px", maxWidth: "380px" }}
          >
            <Text style={{ marginRight: "auto" }}>
              <DrugsIcon /> {parsedDrugs?.length} {t("Drugs")}
            </Text>
          </Tooltip>
        )}
      </>
    );
  } else {
    return <Text></Text>;
  }
};

export const ClaimMedicationsColumnComponent = ({ record }) => {
  const { t } = useTranslation();
  const medications = record.claimMedications?.split(",") || [];

  return (
    <div style={{ textAlign: "center" }}>
      {medications.length === 0 ? (
        ""
      ) : medications.length === 1 ? (
        medications[0]
      ) : (
        <Popover
          trigger="hover"
          content={
            medications.length && (
              <div style={{ maxWidth: "400px", padding: "0 10px" }}>
                <Text>
                  {medications.slice(0, 50).join(", ")}
                  {medications.length > 50 && <span> ...</span>}
                </Text>
              </div>
            )
          }
        >
          <Text style={{ marginRight: "auto" }}>
            <DrugsIcon /> {medications.length} {t("Drugs")}
          </Text>
        </Popover>
      )}
    </div>
  );
};

export const withSorting = (sortingEnabled, tableColumn, { sorterTooltipText, ...rest }, sortDirection) => {
  const [threshold] = useGetSetting(["dashboard.sorting-threshold"]);
  const { t } = useTranslation();
  const selectedViewData = useSelector(selectedViewTableData);
  const { sorter } = selectedViewData;
  const sortingTooltipTitle = threshold && t("sortInfo", { threshold: threshold / 1000 });

  const getSortOrderForDisplay = (sorter, tableColumn) => {
    return sorter?.column?.key === tableColumn?.key ? sorter?.order : null;
  };

  const sortingActionTooltip =
    sorterTooltipText ||
    (() => {
      if (sorter?.column?.key === tableColumn.key) {
        if (sorter?.order === DECEND) {
          return t("columns.click_to_cancel_sorting");
        }
        if (sorter?.order === ASCEND) {
          return t("columns.click_to_sort_descending");
        }
      }
      return t("columns.click_to_sort_ascending");
    })();

  return {
    sorter: sortingEnabled,
    sortDirections: sortDirection ? sortDirection : SORT_DIRECTIONS_DEFAULT,
    sortOrder: getSortOrderForDisplay(sorter, tableColumn),
    ...tableColumn,
    title: () => (
      <div style={{ display: "flex", gap: "5px" }} id={`columnـ${tableColumn.dataIndex}`}>
        <Tooltip title={sortingActionTooltip}>
          <Text style={{ color: GRAY_700 }}>{tableColumn.title}</Text>
        </Tooltip>
        <div>
          {!sortingEnabled && (
            <TitledTooltip text={sortingTooltipTitle}>
              <ExclamationCircleOutlined style={{ fontSize: "12px", alignSelf: "center", color: GRAY_400 }} />
            </TitledTooltip>
          )}
        </div>
      </div>
    ),
    showSorterTooltip: false,
    ...rest,
    defaultSortOrder: sorter?.column && sorter?.column?.key === tableColumn.key && sorter?.order
  };
};

export const getSortingTooltip = (t, sorter, columnKey) => {
  if (sorter?.columnKey === columnKey) {
    if (sorter?.order === DECEND) {
      return t("columns.click_to_cancel_sorting");
    }
    if (sorter?.order === ASCEND) {
      return t("columns.click_to_sort_descending");
    }
  }
  return t("columns.click_to_sort_ascending");
};

export const ViewedAtColumn = ({ record }) => {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Text
        style={{
          fontSize: sizes.medium,
          color: GRAY_900
        }}
      >
        {record.viewedAt ? moment.utc(record.viewedAt).local().format("MM/DD/YYYY h:mm a") : ""}
      </Text>
    </div>
  );
};

export const PatientDOBColumn = ({ record }) => {
  const { t } = useTranslation();

  if (!record.dob || record.dob === t("invalid_date")) {
    return null;
  }

  const formattedDob = moment(record.dob).isValid() ? moment(record.dob).format(DATE_FORMAT) : null;

  return formattedDob ? <div id="patient_dob">{formattedDob}</div> : null;
};

export const PatientSSNColumn = ({ record }) => {
  if (!record.social_security_number) {
    return null;
  }

  return <div id="patient_ssn">{formatSSN(record.social_security_number)}</div>;
};

export const PatientPhoneNumberColumn = ({ record }) => {
  if (!record.phone_number || !record.home_number) {
    return null;
  }

  return <div id="patient_phone_number">{formatPhoneNumber(record.phone_number || record.home_number)}</div>;
};

export const EnrollmentAssigneeColumn = ({ record }) => {
  if (!record?.enrollmentAssignee) return null;

  const getUserOrGroupById = useSelector(selectUserOrGroupById);
  const enrollmentAssigneeTextToDisplay = formatAssignee(getUserOrGroupById(record?.enrollmentAssignee));

  return <div id="patient_enrollment_assignee">{enrollmentAssigneeTextToDisplay}</div>;
};

export const ClaimAssigneeColumn = ({ record }) => {
  if (!record?.claimAssignee) return null;

  const getUserOrGroupById = useSelector(selectUserOrGroupById);
  const claimAssigneeTextToDisplay = formatAssignee(getUserOrGroupById(record?.claimAssignee));

  return <div id="patient_claim_assignee">{claimAssigneeTextToDisplay}</div>;
};
